import { MessageSquare } from "lucide-react";
import { canCreate } from "@/lib/perms";
import { NavLink, useNavigate } from "react-router-dom";
import { ChevronRight, Plus } from "lucide-react";
import { useWorld } from "@/context/WorldContext";
import { splitOrigin } from "@/components/Origin";
import { collectionsOf, groupByCollection, useFold } from "@/lib/collections";

const Entries = ({ items, onNavigate }) => {
  const { basePath, commentCounts = {} } = useWorld();
  return items.map((p) => (
    <NavLink key={p.id} to={`${basePath}/p/${p.id}`} onClick={onNavigate} data-testid={`sidebar-page-${p.id}`}
      className={({ isActive }) => `block truncate text-sm px-2.5 py-1.5 rounded-md border-l-2 transition-colors ${
        isActive ? "bg-amber-500/10 text-amber-200 border-amber-500" : "text-slate-400 border-transparent hover:text-slate-100 hover:bg-veil/[0.03]"}`}>
      {p.title}{commentCounts[p.id] > 0 && <span className="float-right inline-flex items-center gap-0.5 text-[10px] text-slate-500" data-testid={`sidebar-comments-${p.id}`}><MessageSquare className="w-3 h-3" />{commentCounts[p.id]}</span>}
    </NavLink>
  ));
};

const Group = ({ id, name, items, open, onNavigate }) => {
  const { basePath } = useWorld();
  const [folded, toggle] = useFold(`${basePath}:side:${id}`);
  const shut = folded && !open;
  return (
    <div data-testid={`sidebar-collection-${id}`}>
      <button type="button" onClick={toggle} aria-expanded={!shut} data-testid={`sidebar-collection-toggle-${id}`}
        className="w-full flex items-center gap-1 text-xs text-slate-500 hover:text-slate-200 px-1 py-1 transition-colors">
        <ChevronRight className={`w-3 h-3 transition-transform ${shut ? "" : "rotate-90"}`} />
        <span className="truncate">{name}</span><span className="text-slate-600 ml-auto">{items.length}</span>
      </button>
      {!shut && <div className="pl-2 space-y-0.5"><Entries items={items} onNavigate={onNavigate} />{!items.length && <p className="text-xs text-slate-600 italic px-2.5 py-1">Empty</p>}</div>}
    </div>
  );
};

export const SidebarCategory = ({ c, items, searching, onNavigate }) => {
  const { basePath, readOnly, collections, world, profile } = useWorld();
  const nav = useNavigate();
  const [folded, toggle] = useFold(`${basePath}:side:${c.key}`);
  const shut = folded && !searching;
  const cols = collectionsOf(collections, c.key);
  const { groups, rest } = groupByCollection(items, cols);
  const Icon = c.icon;
  return (
    <div data-testid={`sidebar-category-${c.key.toLowerCase()}`}>
      <div className="flex items-center gap-0.5 mb-1.5">
        <button type="button" onClick={toggle} aria-expanded={!shut} title={shut ? "Expand" : "Collapse"} data-testid={`sidebar-fold-${c.key.toLowerCase()}`}
          className="p-0.5 -ml-1.5 rounded text-slate-500 hover:text-slate-200 transition-colors">
          <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${shut ? "" : "rotate-90"}`} />
        </button>
        <NavLink to={`${basePath}/c/${c.key.toLowerCase()}`} onClick={onNavigate} data-testid={`sidebar-category-link-${c.key.toLowerCase()}`}
          style={({ isActive }) => (isActive ? { boxShadow: `inset 0 0 0 1px ${c.hex}66` } : undefined)}
          className={({ isActive }) => `flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-code rounded px-1.5 py-0.5 transition-colors ${c.text} ${isActive ? c.bg : "hover:bg-veil/[0.04]"}`}>
          <Icon className="w-3.5 h-3.5" /> {c.key} <span className="text-slate-600">{items.length}</span>
        </NavLink>
        {!readOnly && canCreate(world, c.key) && (
          <button onClick={() => { nav(`${basePath}/new?category=${c.key}`); onNavigate?.(); }}
            className="ml-auto p-1 rounded text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition-colors" data-testid={`sidebar-add-${c.key.toLowerCase()}`}>
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {!shut && (
        <div className="space-y-0.5 pl-2">
          {profile ? splitOrigin(items, profile).filter((g) => g.items.length).map((g) => (
            <Group key={g.key} id={`${c.key}-${g.key}`} name={g.key === "original" ? "Original" : "From canon"} items={g.items} open={searching} onNavigate={onNavigate} />
          )) : cols.length > 0 ? (
            <>
              {groups.filter((g) => (!readOnly && !profile) || g.items.length).map((g) => <Group key={g.col.id} id={g.col.id} name={g.col.name} items={g.items} open={searching} onNavigate={onNavigate} />)}
              {rest.length > 0 && <Group id={`${c.key}-other`} name="Other entries" items={rest} open={searching} onNavigate={onNavigate} />}
            </>
          ) : <Entries items={items} onNavigate={onNavigate} />}
          {items.length === 0 && <p className="text-xs text-slate-600 italic px-2.5 py-1">Nothing yet</p>}
        </div>
      )}
    </div>
  );
};
